import { useState, useEffect, useMemo } from 'react';
import { Plus, Pencil, Trash2, MapPin, Plane, Ship, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { toast } from 'sonner';
import { getZones, createZone, updateZone, deleteZone } from '../lib/api';

const emptyZone = {
  name: '',
  name_es: '',
  price: 0,
  lat: 18.5,
  lng: -68.5,
  zone_type: 'destination',
  active: true
};

const typeConfig = {
  airport: { label: 'Aeropuerto', icon: Plane, color: 'text-blue-400 bg-blue-500/20' },
  port: { label: 'Puerto', icon: Ship, color: 'text-purple-400 bg-purple-500/20' },
  destination: { label: 'Destino', icon: MapPin, color: 'text-cyan-400 bg-cyan-500/20' },
};

export default function AdminZones() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingZone, setEditingZone] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState(emptyZone);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredZones = useMemo(() => {
    return zones.filter(z => {
      const matchesTab = activeTab === 'all' || z.zone_type === activeTab;
      const matchesSearch = !searchTerm || z.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesTab && matchesSearch;
    });
  }, [zones, activeTab, searchTerm]);

  const counts = useMemo(() => {
    const c = { all: zones.length, airport: 0, port: 0, destination: 0 };
    zones.forEach(z => { c[z.zone_type] = (c[z.zone_type] || 0) + 1; });
    return c;
  }, [zones]);

  const fetchZones = async () => {
    try {
      const response = await getZones();
      setZones(response.data);
    } catch (error) {
      toast.error('Error cargando zonas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchZones(); }, []);

  const openCreate = () => {
    setFormData(emptyZone);
    setEditingZone('new');
  };

  const openEdit = (zone) => {
    setFormData(zone);
    setEditingZone(zone.id);
  };

  const handleSave = async () => {
    try {
      if (editingZone === 'new') {
        await createZone(formData);
        toast.success('Zona creada');
      } else {
        await updateZone(editingZone, formData);
        toast.success('Zona actualizada');
      }
      setEditingZone(null);
      fetchZones();
    } catch (error) {
      toast.error('Error guardando zona');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteZone(deleteId);
      toast.success('Zona eliminada');
      setDeleteId(null);
      fetchZones();
    } catch (error) {
      toast.error('Error eliminando zona');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">Cargando zonas...</div>
      </div>
    );
  }

  const tabs = [
    { key: 'all', label: 'Todas' },
    { key: 'airport', label: 'Aeropuertos' },
    { key: 'port', label: 'Puertos' },
    { key: 'destination', label: 'Destinos' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-zones">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-white lg:hidden">Zonas</h1>
        <Button 
          onClick={openCreate}
          className="bg-gradient-to-r from-orange-600 to-yellow-500 text-black font-bold"
          data-testid="add-zone-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar Zona
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex gap-2 flex-wrap">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                  : 'bg-white/5 text-slate-400 border border-white/5 hover:bg-white/10'
              }`}
              data-testid={`zone-tab-${tab.key}`}
            >
              {tab.label} ({counts[tab.key]})
            </button>
          ))}
        </div>
        <div className="relative flex-1 sm:max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Buscar zona..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white/5 border-white/10 text-white pl-10"
            data-testid="zone-search"
          />
        </div>
      </div>

      {/* Zones Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredZones.map((zone) => {
          const config = typeConfig[zone.zone_type] || typeConfig.destination;
          const Icon = config.icon;
          return (
            <div 
              key={zone.id} 
              className={`admin-card p-6 ${!zone.active && 'opacity-60'}`}
              data-testid={`zone-card-${zone.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm">{zone.name}</h3>
                    {zone.name_es && zone.name_es !== zone.name && (
                      <p className="text-xs text-slate-500">{zone.name_es}</p>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${config.color}`}>
                      {typeConfig[zone.zone_type]?.label || zone.zone_type}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => openEdit(zone)} data-testid={`edit-zone-${zone.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => setDeleteId(zone.id)} data-testid={`delete-zone-${zone.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-orange-400">${zone.price}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  zone.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {zone.active ? 'Activo' : 'Inactivo'}
                </span>
              </div>

              <div className="mt-3 text-xs text-slate-500">
                Coordenadas: {zone.lat?.toFixed(4)}, {zone.lng?.toFixed(4)}
              </div>
            </div>
          );
        })}
      </div>

      {filteredZones.length === 0 && (
        <div className="admin-card p-12 text-center">
          <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">No se encontraron zonas</p>
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingZone} onOpenChange={() => setEditingZone(null)}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>
              {editingZone === 'new' ? 'Agregar Nueva Zona' : 'Editar Zona'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Tipo de Zona</label>
              <Select value={formData.zone_type} onValueChange={(v) => setFormData({ ...formData, zone_type: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="zone-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10">
                  <SelectItem value="airport" className="text-white hover:bg-white/10">Aeropuerto</SelectItem>
                  <SelectItem value="port" className="text-white hover:bg-white/10">Puerto</SelectItem>
                  <SelectItem value="destination" className="text-white hover:bg-white/10">Destino</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Nombre (EN)</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="zone-name-input"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Nombre (ES)</label>
                <Input
                  value={formData.name_es || ''}
                  onChange={(e) => setFormData({ ...formData, name_es: e.target.value })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-slate-400">Precio Base (USD)</label>
              <Input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="zone-price-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Latitud</label>
                <Input
                  type="number"
                  step="0.0001"
                  value={formData.lat}
                  onChange={(e) => setFormData({ ...formData, lat: parseFloat(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Longitud</label>
                <Input
                  type="number"
                  step="0.0001"
                  value={formData.lng}
                  onChange={(e) => setFormData({ ...formData, lng: parseFloat(e.target.value) || 0 })}
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm text-slate-400">Activo</label>
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                data-testid="zone-active-switch"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingZone(null)} className="border-white/10 text-white hover:bg-white/5">
              Cancelar
            </Button>
            <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-zone-btn">
              Guardar Zona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Eliminar Zona?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esto también afectará todos los hoteles en esta zona. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
